import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@pentabd/ui";

import {
  CancelSuspendModal,
  DisciplineProsecutionForm,
  DisciplineTable,
} from "./components";

import { useDiscipline } from "@hooks/HRM/employee/useDiscipline";
import { useUser } from "@hooks/useUser";

import { getAccess } from "@helpers/permissionController";

import {
  DISCIPLINE,
  DisciplineFormDataType,
  disciplineValidation,
} from "@validations/HRM/employee/disciplineValidation";
import { convertDocumentData } from "@utils/documentDataConvert";

export interface DisciplinePropsType {
  id: number | null;
  setIsSuccessModalOpen: (arg: boolean) => void;
  setIsErrorModalOpen: (arg: boolean) => void;
  isProfileDisabled: boolean;
}

function Discipline({
  id,
  setIsErrorModalOpen,
  setIsSuccessModalOpen,
  isProfileDisabled,
}: DisciplinePropsType) {
  const [isSuspend, setIsSuspend] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [updateDisciplineData, setUpdateDisciplineData] = useState<any>(null);

  const { t } = useTranslation();
  const { user } = useUser();
  const { realm_roles: roles } = user || {};
  const {
    disciplines,
    createDisciplineRequestOngoing,
    createDisciplineSuccess,
    deleteDisciplineSuccess,
    createDisciplineFailed,
    deleteDisciplineFailed,
    updateDisciplineSuccess,
    updateDisciplineFailed,
    createDiscipline,
    updateDiscipline,
    deleteDisciplineTable,
  } = useDiscipline(id);

  const onOpen = (data: any) => {
    setIsOpen(true);
    setUpdateDisciplineData(data);
  };

  const onClose = () => {
    setIsOpen(false);
    setUpdateDisciplineData(null);
  };

  const methods = useForm<DisciplineFormDataType>({
    resolver: yupResolver(disciplineValidation),
  });

  useEffect(() => {
    if (createDisciplineSuccess || updateDisciplineSuccess) {
      methods.reset();
      setIsSuspend(false);
      onClose();
    }

    setIsSuccessModalOpen(
      createDisciplineSuccess ||
        updateDisciplineSuccess ||
        deleteDisciplineSuccess,
    );
    setIsErrorModalOpen(
      createDisciplineFailed ||
        updateDisciplineFailed ||
        deleteDisciplineFailed,
    );
  }, [
    createDisciplineSuccess,
    deleteDisciplineSuccess,
    createDisciplineFailed,
    deleteDisciplineFailed,
    updateDisciplineSuccess,
    updateDisciplineFailed,
    methods,
  ]);

  const onSubmit: SubmitHandler<DisciplineFormDataType> = (data: any) => {
    createDiscipline(convertDocumentData(data));
  };

  const onUpdateDiscipline = (data: any) => {
    updateDiscipline({
      disciplineId: updateDisciplineData?.id || null,
      isSuspendEnded: true,
      suspendFinishedDate: data[DISCIPLINE.SUSPEND_FINISH_DATE],
    });
  };

  const onIsSuspend = (data: boolean) => {
    setIsSuspend(data);
  };

  return (
    <div className="d-flex flex-column gap-10">
      <DisciplineTable
        disciplineInfo={disciplines || []}
        deleteDisciplineTable={deleteDisciplineTable}
        onUpdateDiscipline={onUpdateDiscipline}
        onOpen={onOpen}
        isProfileDisabled={isProfileDisabled}
      />

      {getAccess(roles, "PUNISHMENT_UPDATE") && !isProfileDisabled && (
        <FormProvider {...methods}>
          <form className="container" onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="pt-10">
              <DisciplineProsecutionForm
                isSuspend={isSuspend}
                onIsSuspend={onIsSuspend}
              />
              {/* This is the regular submit button */}
              <div className="row py-10">
                <div className="text-end col-12 col-lg-10">
                  <Button
                    key="discipline_save"
                    htmlType="submit"
                    fill="outline"
                    type="primary"
                    size="sm"
                    loading={createDisciplineRequestOngoing}
                  >
                    {t("EMPLOYEE.GENERAL_INFO.FORM.SAVE_BUTTON")}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      )}

      {isOpen && (
        <CancelSuspendModal
          isOpen={isOpen}
          onClose={onClose}
          onUpdateDiscipline={onUpdateDiscipline}
        />
      )}
    </div>
  );
}

export default Discipline;
